import { renderApproximate } from "@defibrillator/preview-engine";
import type { LocalPresetRecord } from "../../shared/types.js";
import { encodeWavMonoBlob } from "./encode-wav.js";
import { saveLocalPreset } from "./idb.js";
import { pollJobPreview } from "./poll-job-preview.js";

export type ResolvedLibraryPreview = {
  url: string;
  source: "local" | "renderer" | "client";
  record: LocalPresetRecord;
};

export function previewMatchesRecord(record: LocalPresetRecord): boolean {
  if (!record.previewBlob || record.previewBlob.size <= 0) return false;
  if (!record.contentHash) {
    // Unverified legacy cache — only trust when no content hash is known yet.
    return !record.previewContentHash;
  }
  return record.previewContentHash === record.contentHash;
}

/** Prefer version-matched cached WAV → renderer job → client approximate render. */
export async function resolveLibraryPreview(
  record: LocalPresetRecord,
  options?: { signal?: AbortSignal },
): Promise<ResolvedLibraryPreview> {
  if (previewMatchesRecord(record) && record.previewBlob) {
    return {
      url: URL.createObjectURL(record.previewBlob),
      source: "local",
      record,
    };
  }

  if (record.jobId) {
    try {
      const status = await pollJobPreview({
        jobId: record.jobId,
        signal: options?.signal,
        timeoutMs: 15_000,
      });
      if (status.preview.ready) {
        const blob = await fetchPreviewUrl(status.preview.url, options?.signal);
        const updated = await cachePreview(record, blob);
        return {
          url: URL.createObjectURL(blob),
          source: "renderer",
          record: updated,
        };
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      // Fall through to client approximate render.
      console.warn("[library-preview] renderer fetch failed, using client approx", err);
    }
  }

  if (options?.signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const approx = renderApproximate(record.model);
  const blob = encodeWavMonoBlob(approx.samples, approx.sampleRate);
  const updated = await cachePreview(record, blob);
  return {
    url: URL.createObjectURL(blob),
    source: "client",
    record: updated,
  };
}

/** Poll job then download preview WAV bytes. */
export async function fetchPreviewBlob(
  jobId: string,
  options?: {
    signal?: AbortSignal;
    onTick?: Parameters<typeof pollJobPreview>[0]["onTick"];
  },
): Promise<{ blob: Blob; status: Awaited<ReturnType<typeof pollJobPreview>> }> {
  const status = await pollJobPreview({
    jobId,
    signal: options?.signal,
    onTick: options?.onTick,
  });
  if (!status.preview.ready) {
    throw new Error("Preview not ready");
  }
  const blob = await fetchPreviewUrl(status.preview.url, options?.signal);
  return { blob, status };
}

export async function cachePreview(
  record: LocalPresetRecord,
  blob: Blob,
): Promise<LocalPresetRecord> {
  const updated: LocalPresetRecord = {
    ...record,
    previewBlob: blob,
    previewContentHash: record.contentHash,
    updatedAt: new Date().toISOString(),
  };
  await saveLocalPreset(updated);
  return updated;
}

/**
 * Mint endpoint returns either:
 * - JSON `{ url }` with a short-lived R2 signed GET (preferred), or
 * - raw WAV bytes for insecure-dev filesystem fallback.
 * Never cache signed query strings in IndexedDB — only the WAV blob.
 */
async function fetchPreviewUrl(url: string, signal?: AbortSignal): Promise<Blob> {
  const mint = await fetch(url, { credentials: "include", signal });
  if (!mint.ok) {
    throw new Error(`Preview fetch failed (${mint.status})`);
  }

  const contentType = mint.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const body = (await mint.json()) as { url?: string };
    if (!body.url || typeof body.url !== "string") {
      throw new Error("Preview signed URL missing");
    }
    const remote = await fetch(body.url, { signal });
    if (!remote.ok) {
      throw new Error(`Preview R2 fetch failed (${remote.status})`);
    }
    return remote.blob();
  }

  return mint.blob();
}
